import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  VictoryAxis, VictoryBar, VictoryChart, VictoryLabel, VictoryTooltip,
} from 'victory';
import APP_FONT from '../../helpers/font';

// Bar chart of a count per day ({ date: 'YYYY-MM-DD', count }), with the
// value on each bar (when there is room) and a tooltip on hover.

const AXIS = {
  axis: { stroke: '#D7DEEA' },
  tickLabels: {
    fontSize: 11, fill: '#8A94A6', padding: 6, fontFamily: APP_FONT,
  },
  grid: { stroke: 'transparent' },
};
const Y_AXIS = {
  ...AXIS,
  axis: { stroke: 'transparent' },
  grid: { stroke: '#EEF1F6' },
};
const PADDING = {
  top: 22, bottom: 34, left: 40, right: 12,
};

// Width of the element, kept up to date (the chart is drawn at real size so
// its labels don't shrink with the card).
const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

export default function TrendChart({
  days = [], color = '#4582FF', unit = ['activity', 'activities'],
}) {
  const [ref, width] = useWidth();
  const data = days.map((d, i) => ({
    x: i + 1,
    y: d.count,
    date: moment(d.date, 'YYYY-MM-DD').format('ddd, D MMM'),
  }));
  const max = Math.max(0, ...days.map((d) => d.count));
  // One date label per ~70px; values on the bars when each bar has ~18px.
  const step = Math.max(1, Math.ceil(days.length / Math.max(2, Math.floor(width / 70))));
  const slot = days.length ? (width - PADDING.left - PADDING.right) / days.length : 0;
  const showValues = slot >= 18;

  return (
    <div ref={ref}>
      {width ? (
        <VictoryChart
          responsive={false}
          height={250}
          width={width}
          padding={PADDING}
          domainPadding={{ x: 10 }}
          domain={{ y: [0, Math.max(4, Math.ceil(max * 1.12))] }}
        >
          <VictoryAxis
            style={AXIS}
            tickValues={data.map((d) => d.x)}
            tickFormat={(t) => ((t - 1) % step === 0 && days[t - 1]
              ? moment(days[t - 1].date, 'YYYY-MM-DD').format('D MMM') : '')}
          />
          <VictoryAxis dependentAxis style={Y_AXIS} tickFormat={(t) => (Number.isInteger(t) ? t : '')} />

          {/* Bars with their value on top. */}
          <VictoryBar
            data={data}
            barRatio={0.7}
            cornerRadius={{ top: 2 }}
            style={{ data: { fill: color } }}
            labels={showValues ? ({ datum }) => (datum.y ? datum.y.toLocaleString('en-IN') : '') : undefined}
            labelComponent={(
              <VictoryLabel
                dy={-3}
                style={{
                  fontSize: 10, fontWeight: 600, fill: '#5B6472', fontFamily: APP_FONT,
                }}
              />
            )}
          />

          {/* Transparent copy on top for the hover tooltip: count, then the day. */}
          <VictoryBar
            data={data}
            barRatio={0.7}
            style={{ data: { fill: 'transparent' } }}
            labels={({ datum }) => [
              `${datum.y.toLocaleString('en-IN')} ${datum.y === 1 ? unit[0] : unit[1]}`,
              datum.date,
            ]}
            labelComponent={(
              <VictoryTooltip
                constrainToVisibleArea
                cornerRadius={8}
                pointerLength={6}
                pointerWidth={12}
                flyoutPadding={{
                  top: 7, bottom: 7, left: 11, right: 11,
                }}
                flyoutStyle={{ fill: '#1F2733', stroke: 'none' }}
                style={[
                  {
                    fill: '#FFFFFF', fontSize: 13, fontWeight: 600, fontFamily: APP_FONT,
                  },
                  {
                    fill: '#C9D1E1', fontSize: 11, fontFamily: APP_FONT,
                  },
                ]}
              />
            )}
          />
        </VictoryChart>
      ) : null}
    </div>
  );
}
